import { useRoute } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { htmlEditorHelperActions } from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/useHTMLEditor';
import useTabs from 'src/components/10-atoms/tabs/useTabs';
import { overlayHelperViewActions } from 'src/components/20-helper-views/overlay/useOverlay';
import { promptHelperActions } from 'src/components/20-helper-views/prompt/helperActions';
import { snackbarHelperActions } from 'src/components/20-helper-views/snackbar/useSnackbar';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import useEffectUntilDone from 'src/hooks/useEffectUtils/useEffectUntilDone';
import { playlistPlayerHelperViewActions } from 'src/lib/playlist/hooks/usePlaylistPlayer';
import { ProcessorType } from 'src/lib/playlist/types';
import useIsSuperAdmin from 'src/modules/authentication/hooks/useIsSuperAdmin';
import useI18n from 'src/modules/translations/hooks/useI18n';
import isNot from 'src/utils/array/isNot';
import { useCreateAnswerMutation } from '../../graphql/answer/create';
import { useUpdateAnswerMutation } from '../../graphql/answer/update';
import AnswerUtils from '../../graphql/answer/utils';
import { NodeType } from '../../graphql/common/types/node';
import { useQuestionAnswerContributorsQuery } from '../../graphql/question/getQuestionAnswerContributors';
import { useQuestionDetailsQuery } from '../../graphql/question/getQuestionDetails';
import { useEPUpdateQuestionBookmark } from '../../graphql/question/updateQuestionBookmark';
import { useEPUpdateQuestionRead } from '../../graphql/question/updateQuestionRead';
import EditQuestionOverlay from './containers/EditQuestionOverlay';
export default function useData() {
    const route = useRoute();
    const { question, questionID } = route.params;
    const shouldFetchEF = question?.type
        ? question.type === NodeType.WRITTEN_QUESTION
        : true;
    const { translate } = useI18n();
    const [questionDetailsQueryState, questionDetailsQueryActions] = useQuestionDetailsQuery({
        variables: {
            questionID,
            withEF: shouldFetchEF,
        },
    });
    const [questionAnswerContributorsQueryState] = useQuestionAnswerContributorsQuery({
        variables: {
            questionID,
        },
    });
    const answerContributors = useMemo(() => questionAnswerContributorsQueryState.data?.ep_questionDetails.answer?.data.answerHistory.map(history => history.author) ?? [], [questionAnswerContributorsQueryState.data]);
    const [tabsState, tabsActions] = useTabs();
    useEffectUntilDone((done) => {
        if (questionDetailsQueryState.data) {
            const config = questionDetailsQueryState.data.ep_questionDetails.config;
            const shouldShowEF = questionDetailsQueryState.data.ep_questionDetails.type ===
                NodeType.WRITTEN_QUESTION
                ? (config?.WRITTEN_QUESTION?.hasExaminersFeedback ?? false)
                : false;
            tabsActions.setTabs([
                {
                    id: 'ANSWER',
                    label: translate('Answer'),
                },
                shouldShowEF && {
                    id: 'EXAMINERS_FEEDBACK',
                    label: translate("Examiner's Feedback"),
                },
            ].filter(isNot(false)));
            tabsActions.setActiveIndex(0);
            done();
        }
    }, [questionDetailsQueryState.data]);
    const [createAnswerMutationState, createAnswerMutationActions] = useCreateAnswerMutation();
    const handleCreateAnswer = useStableCallback(() => {
        htmlEditorHelperActions.show({
            title: translate('Write Answer'),
            html: '',
            placeholder: translate('Write your Answer here'),
            onSubmit(data) {
                if (!data.hasContent) {
                    return;
                }
                createAnswerMutationActions.create({
                    variables: {
                        questionID,
                        data: {
                            answer: data.html,
                        },
                    },
                });
            },
        });
    });
    const [updateAnswerMutationState, updateAnswerMutationActions] = useUpdateAnswerMutation();
    const handleUpdateAnswer = useStableCallback(() => {
        const questionDetails = questionDetailsQueryState.data?.ep_questionDetails;
        if (!questionDetails) {
            return;
        }
        const currentAnswer = AnswerUtils.getCurrentAnswer(questionDetails.answer)?.answer ?? '';
        htmlEditorHelperActions.show({
            title: translate('Edit Answer'),
            placeholder: translate('Write your answer here'),
            html: currentAnswer,
            async onSubmit({ html: newAnswer, hasChanges }) {
                if (!hasChanges) {
                    snackbarHelperActions.show({ message: 'No changes to submit' });
                    return;
                }
                const reason = await promptHelperActions.show({
                    title: translate('Comments for the reviewer'),
                    placeholder: translate('Incorrect formula was used, hence fixed it'),
                });
                if (!reason) {
                    return;
                }
                updateAnswerMutationActions.submit({
                    variables: {
                        questionID: questionDetails.id,
                        data: {
                            answer: newAnswer,
                            comments: reason,
                        },
                    },
                });
            },
        });
    });
    const handlePlaylistPress = useStableCallback(() => {
        playlistPlayerHelperViewActions.overrideQueue({
            list: [
                {
                    processorType: ProcessorType.EP_QUESTION_DETAILS,
                    meta: route.params,
                },
            ],
            activeIndex: 0,
        });
    });
    const handleQuestionEditPress = useStableCallback(() => {
        if (questionDetailsQueryState.data) {
            const questionDetails = questionDetailsQueryState.data.ep_questionDetails;
            overlayHelperViewActions.show({
                component: (React.createElement(EditQuestionOverlay, { question: questionDetails, onClose: overlayHelperViewActions.hide, onComplete: overlayHelperViewActions.hide })),
            });
        }
    });
    const isSuperAdmin = useIsSuperAdmin();
    const markAsDuplicateVisible = questionDetailsQueryState.data?.ep_questionDetails && isSuperAdmin;
    const [updateReadMutationState, updateReadMutationActions] = useEPUpdateQuestionRead({
        allowOnlyRegisteredUser: false,
    });
    const [updateBookmarkMutationState, updateBookmarkMutationActions] = useEPUpdateQuestionBookmark();
    const state = {
        createAnswerMutation: createAnswerMutationState,
        updateAnswerMutation: updateAnswerMutationState,
        tabs: tabsState,
        questionDetailsQuery: questionDetailsQueryState,
        markAsDuplicateVisible,
        updateReadMutation: updateReadMutationState,
        updateBookmarkMutation: updateBookmarkMutationState,
        answerContributors,
    };
    const actions = {
        tabs: tabsActions,
        questionDetailsQuery: questionDetailsQueryActions,
        handleCreateAnswer,
        handleUpdateAnswer,
        createAnswerMutation: createAnswerMutationActions,
        handlePlaylistPress,
        handleQuestionEditPress,
        updateReadMutation: updateReadMutationActions,
        updateBookmarkMutation: updateBookmarkMutationActions,
    };
    return [state, actions];
}
